import { createSVGElement } from '@/utils/svg-kit';
import type { Bounds, Optional } from '@/version-types';
import { createGlyphNode, wrapTransform, type GlyphNodeSource } from './nodes';

/**
 * 一条带富文本分段归属和可选外层变换的绘制事实。
 *
 * pathBounds 是当前 render 内按唯一 path 解析的原始轮廓边界，只供 geometry 和节点变换复用；
 * 非 path 指令与字符 marker 不携带该字段。
 */
export type GlyphItem = {
    glyph: GlyphNodeSource;
    spanIndex: number;
    transform?: string;
    pathBounds?: Bounds;
};

/**
 * 当前一轮正文使用的累计描边。
 */
export type GlyphStroke = Readonly<{ color: string; width: number }>;

/**
 * 使用同一字形流绘制累计描边预绘制层。
 *
 * path 节点创建会复用 GlyphItem 上的本轮 bounds，竖排旋转无需再次解析相同 SVG path。
 *
 * @param target - 正文或字符 marker 的绘制挂载点。
 * @param glyphs - 当前目标的有序字形流。
 * @param strokes - 已累计宽度的可见描边。
 * @returns void，通过副作用追加描边 group。
 */
export const drawStrokes = (
    target: SVGElement,
    glyphs: readonly GlyphItem[],
    strokes: readonly GlyphStroke[],
): void => {
    for (let index = strokes.length - 1; index >= 0; index -= 1) {
        const stroke = strokes[index];
        const group = createSVGElement('g', {
            stroke: stroke.color,
            'stroke-width': stroke.width * 2,
            'stroke-linejoin': 'round',
            'data-stroke-layer': index,
        });
        glyphs.forEach(({ glyph, transform, pathBounds }) => {
            if (
                glyph.kind !== 'marker' &&
                glyph.kind !== 'path' &&
                glyph.kind !== 'decorationRect' &&
                (glyph.kind !== 'delegated' || glyph.reason === 'emoji')
            ) {
                return;
            }
            const node = createGlyphNode(glyph, pathBounds);
            if (!node) return;
            node.removeAttribute('fill');
            if (glyph.kind === 'marker') {
                // marker 继续保留既有的直接描边属性；正文仍使用 group 继承，二者共享同一个绘制循环。
                node.setAttribute('fill', 'none');
                node.setAttribute('stroke', stroke.color);
                node.setAttribute('stroke-width', String(stroke.width * 2));
                node.setAttribute('stroke-linejoin', 'round');
            }
            group.appendChild(wrapTransform(node, transform));
        });
        target.appendChild(group);
    }
};

/**
 * 按 span 分组绘制正文或字符 marker，并跳过已由 overlay 消费的普通轮廓。
 *
 * path 节点创建会复用 GlyphItem 上的本轮 bounds；该优化只消除重复边界解析，不改变节点顺序、
 * span 分组或 overlay 仲裁。
 *
 * @param target - 正文或字符 marker 的绘制挂载点。
 * @param glyphs - 当前目标的有序字形流。
 * @param overlayActive - 纹理或渐变是否已通过 mask 消费普通 path。
 * @param fillId - 字符 marker 需要复用的纹理或渐变 ID；正文不传。
 * @returns void，通过副作用追加最终字形节点。
 */
export const drawGlyphs = (
    target: SVGElement,
    glyphs: readonly GlyphItem[],
    overlayActive: boolean,
    fillId?: Optional<string>,
): void => {
    const groups: Array<SVGGElement | null> = [];
    glyphs.forEach(({ glyph, spanIndex, transform, pathBounds }) => {
        if (glyph.kind !== 'marker' && overlayActive && (glyph.kind === 'path' || glyph.kind === 'decorationRect')) {
            return;
        }
        const node = createGlyphNode(glyph, pathBounds);
        if (!node) return;
        if (glyph.kind === 'marker' && fillId) node.setAttribute('fill', `url(#${fillId})`);
        if (glyph.kind !== 'marker' && overlayActive && glyph.kind === 'delegated' && glyph.reason !== 'emoji') {
            node.setAttribute('fill', 'rgba(0,0,0,0)');
        }
        if (spanIndex < 0 || glyph.kind === 'marker' || glyph.kind === 'decorationRect') {
            target.appendChild(wrapTransform(node, transform));
            return;
        }
        let group = groups[spanIndex];
        if (!group) {
            group = createSVGElement('g', {
                'data-span-index': spanIndex,
                ...(transform ? { transform } : {}),
            });
            target.appendChild(group);
            groups[spanIndex] = group;
        }
        group.appendChild(node);
    });
};
